// File: src/pages/LeaguePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchLeagueTable, fetchLeagueFixtures, fetchLeagueStats, fetchTopScorers, getMyPreferences, saveMyPreferences } from "../api";
import SportsNewsSection from "../components/SportsNewsSection";
import WorldCupHub from "./WorldCupHub";
import { getLeagueCodeFromAny, getLeagueIdFromAny, getLeagueNameFromAny } from "../utils/leagueRouting";
import { formatLeagueName } from "../utils/competitionNames";
import { slugifyTeamName } from "../utils/slugify";
import {
  WORLD_CUP_CODE,
  WORLD_CUP_SEASON,
  buildWorldCupTeamGroupMap,
  isOfficialWorldCupGroup,
  isWorldCupCompetition,
  normalizeWorldCupGroups,
  normalizeWorldCupTeamName,
} from "../utils/worldCup";
import styles from "../styles/LeaguePage.module.css";

const FINISHED_STATUSES = ["FT", "AET", "PEN", "CANC", "ABD", "AWD", "WO"];
const LIVE_STATUSES = ["1H", "2H", "HT", "ET", "BT", "LIVE"];
const KNOCKOUT_ROUND_HINTS = [
  "round of 32",
  "round of 16",
  "32nd finals",
  "16th finals",
  "8th finals",
  "quarter",
  "semi",
  "third",
  "final",
];
const KNOCKOUT_ROUNDS = [
  { key: "round32", title: "Round of 32", short: "R32" },
  { key: "round16", title: "Round of 16", short: "R16" },
  { key: "quarter", title: "Quarter-finals", short: "QF" },
  { key: "semi", title: "Semi-finals", short: "SF" },
  { key: "final", title: "Final", short: "Final" },
];

const isFinishedStatus = (status = "") => FINISHED_STATUSES.includes(String(status).toUpperCase());
const isLiveStatus = (status = "") => LIVE_STATUSES.includes(String(status).toUpperCase());
const extractRoundNumber = (round = "") => {
  const match = String(round).match(/(\d+)(?!.*\d)/);
  return match ? Number(match[1]) : null;
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeTablePayload = (payload, { preserveNulls = false } = {}) => {
  const table = Array.isArray(payload) ? payload : payload?.table || [];
  const emptyStat = preserveNulls ? null : 0;
  const splitStats = (source = {}) => ({
    played: firstDefined(source.played, source.p, source.mp, source.matches, emptyStat),
    win: firstDefined(source.win, source.w, emptyStat),
    draw: firstDefined(source.draw, source.d, emptyStat),
    lose: firstDefined(source.lose, source.loss, source.l, emptyStat),
    gf: firstDefined(source.gf, source.goals_for, source.goals?.for, emptyStat),
    ga: firstDefined(source.ga, source.goals_against, source.goals?.against, emptyStat),
    points: firstDefined(source.points, source.pts, emptyStat),
    form: firstDefined(source.form, source.recent_form, ""),
  });
  return table.map((row) => {
    const teamName = firstDefined(row.team?.name, row.team_name, row.name, typeof row.team === "string" ? row.team : null, "Unknown");
    const all = firstDefined(row.all, row.overall, row.stats?.all, {});
    const home = splitStats(firstDefined(row.home, row.home_stats, row.stats?.home, row.home_record, {}));
    const away = splitStats(firstDefined(row.away, row.away_stats, row.stats?.away, row.away_record, {}));
    const last5 = splitStats(
      firstDefined(
        row.last5,
        row.last_5,
        row.last_five,
        row.form_5,
        row.last_five_stats,
        row.stats?.last5,
        {}
      )
    );
    return {
      position: firstDefined(row.position, row.rank, row.pos, "-"),
      team: teamName,
      team_id: firstDefined(row.team_id, row.provider_team_id, row.id, row.team?.id, null),
      team_logo: firstDefined(
        row.team_logo,
        row.logo,
        row.team?.logo,
        row.team_badge,
        row.badge,
        row.team?.badge,
        null
      ),
      played: firstDefined(row.played, row.p, row.mp, all.played, emptyStat),
      win: firstDefined(row.win, row.w, all.win, emptyStat),
      draw: firstDefined(row.draw, row.d, all.draw, emptyStat),
      lose: firstDefined(row.lose, row.loss, row.l, all.lose, emptyStat),
      gf: firstDefined(row.gf, row.goals_for, row.goals?.for, all.goals?.for, emptyStat),
      ga: firstDefined(row.ga, row.goals_against, row.goals?.against, all.goals?.against, emptyStat),
      points: firstDefined(row.points, row.pts, emptyStat),
      form: firstDefined(row.form, row.recent_form, ""),
      group: firstDefined(row.group, row.stage_group, ""),
      description: firstDefined(row.description, row.note, ""),
      home,
      away,
      last5,
      next_opponent: firstDefined(
        row.next_opponent,
        row.next_team,
        row.next?.opponent,
        row.next?.team,
        row.next_fixture?.opponent,
        row.next_fixture?.opponent_name
      ),
      next_opponent_logo: firstDefined(
        row.next_opponent_logo,
        row.next_team_logo,
        row.next?.opponent_logo,
        row.next?.logo,
        row.next_fixture?.opponent_logo
      ),
    };
  });
};

const normalizeFixturesPayload = (payload) => {
  const fixtures = payload?.fixtures || [];
  return fixtures.map((fixture) => ({
    internal_route_id: firstDefined(
      fixture.csb_fixture_id,
      fixture.app_fixture_id,
      fixture.internal_fixture_id,
      fixture.db_fixture_id,
      fixture.local_fixture_id,
      fixture.internal_id
    ),
    route_id: firstDefined(
      fixture.csb_fixture_id,
      fixture.app_fixture_id,
      fixture.internal_fixture_id,
      fixture.db_fixture_id,
      fixture.id,
      fixture.fixture_id,
      fixture.provider_fixture_id
    ),
    id: firstDefined(fixture.id, fixture.fixture_id, fixture.provider_fixture_id),
    provider_fixture_id: firstDefined(fixture.provider_fixture_id, fixture.api_fixture_id, fixture.external_fixture_id),
    round: firstDefined(fixture.round, fixture.league_round, fixture.week, "Unknown"),
    date: firstDefined(fixture.date, fixture.kickoff_utc, fixture.kickoff, fixture.timestamp),
    timestamp: firstDefined(fixture.timestamp, fixture.fixture?.timestamp, null),
    status: firstDefined(fixture.status, fixture.status_short, fixture.match_status, ""),
    home_team: firstDefined(fixture.home_team, fixture.home_name, fixture.home?.name, "Home"),
    away_team: firstDefined(fixture.away_team, fixture.away_name, fixture.away?.name, "Away"),
    home_team_id: firstDefined(
      fixture.home_team_id,
      fixture.provider_home_team_id,
      fixture.home?.id,
      fixture.teams?.home?.id,
      null
    ),
    away_team_id: firstDefined(
      fixture.away_team_id,
      fixture.provider_away_team_id,
      fixture.away?.id,
      fixture.teams?.away?.id,
      null
    ),
    home_logo: firstDefined(
      fixture.home_logo,
      fixture.home_team_logo,
      fixture.home?.logo,
      fixture.teams?.home?.logo,
      fixture.home_badge,
      fixture.home?.badge,
      fixture.teams?.home?.badge,
      null
    ),
    away_logo: firstDefined(
      fixture.away_logo,
      fixture.away_team_logo,
      fixture.away?.logo,
      fixture.teams?.away?.logo,
      fixture.away_badge,
      fixture.away?.badge,
      fixture.teams?.away?.badge,
      null
    ),
    home_score: firstDefined(fixture.home_score, fixture.goals?.home, fixture.score?.fulltime?.home, null),
    away_score: firstDefined(fixture.away_score, fixture.goals?.away, fixture.score?.fulltime?.away, null),
    venue: firstDefined(fixture.venue, fixture.venue_name, fixture.fixture?.venue?.name, fixture.stadium, ""),
    city: firstDefined(fixture.city, fixture.venue_city, fixture.fixture?.venue?.city, ""),
    group: firstDefined(fixture.group, fixture.league_group, fixture.stage_group, ""),
  }));
};

const toDateValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") {
    const ms = value > 1e12 ? value : value * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(String(value));
  if (!Number.isNaN(date.getTime())) return date;

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    const ms = numeric > 1e12 ? numeric : numeric * 1000;
    const numericDate = new Date(ms);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }
  return null;
};

const resolveLeagueDisplayName = ({ code, name, id }) => {
  const resolvedCode = getLeagueCodeFromAny({ code, name, id });
  if (resolvedCode) return formatLeagueName(resolvedCode, "League");

  const resolvedName = getLeagueNameFromAny({ code, name, id }) || name || code;
  if (!resolvedName) return "League";
  return formatLeagueName(resolvedName, "League");
};

const fallbackLogoForTeam = (teamName) =>
  teamName ? `/logos/${slugifyTeamName(teamName)}.png` : null;

const applyLogoFallback = (event, teamName) => {
  const fallback = fallbackLogoForTeam(teamName);
  if (!fallback) {
    event.currentTarget.style.display = "none";
    return;
  }
  if (event.currentTarget.dataset.fallbackApplied === "1") {
    event.currentTarget.style.display = "none";
    return;
  }
  event.currentTarget.dataset.fallbackApplied = "1";
  event.currentTarget.src = fallback;
};

const getLeagueLogo = (...payloads) =>
  firstDefined(
    ...payloads.map((payload) =>
      firstDefined(
        payload?.league_logo,
        payload?.logo,
        payload?.league?.logo,
        payload?.competition?.logo,
        payload?.response?.[0]?.league?.logo
      )
    )
  );

const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const buildPlayerLeaderboards = (rawStats) => {
  const players = Array.isArray(rawStats?.response)
    ? rawStats.response
    : Array.isArray(rawStats?.players)
    ? rawStats.players
    : [];

  if (!players.length) return [];

  const leaderboardConfigs = [
    {
      key: "top_scorer",
      title: "Top Scorer",
      unit: "goals",
      getValue: (s) => asNumber(s?.goals?.total),
    },
    {
      key: "top_assists",
      title: "Top Assists",
      unit: "assists",
      getValue: (s) => asNumber(s?.goals?.assists),
    },
    {
      key: "goals_plus_assists",
      title: "Goals + Assists",
      unit: "goal contributions",
      getValue: (s) => asNumber(s?.goals?.total) + asNumber(s?.goals?.assists),
    },
    {
      key: "most_yellow_cards",
      title: "Most Yellow Cards",
      unit: "yellow cards",
      getValue: (s) => asNumber(s?.cards?.yellow),
    },
    {
      key: "most_red_cards",
      title: "Most Red Cards",
      unit: "red cards",
      getValue: (s) => asNumber(s?.cards?.red),
    },
    {
      key: "most_shots",
      title: "Most Shots",
      unit: "shots",
      getValue: (s) => asNumber(s?.shots?.total),
    },
    {
      key: "most_shots_on_target",
      title: "Most On Target",
      unit: "on target",
      getValue: (s) => asNumber(s?.shots?.on),
    },
    {
      key: "most_penalties_scored",
      title: "Most Penalties",
      unit: "penalties",
      getValue: (s) => asNumber(s?.penalty?.scored),
    },
  ];

  const toLeaderboardEntry = (entry, value) => {
    const stat = entry?.statistics?.[0] || {};
    return {
      player: entry?.player?.name || "Unknown",
      playerPhoto: entry?.player?.photo || null,
      playerId: entry?.player?.id || null,
      team: stat?.team?.name || "",
      teamId: stat?.team?.id || null,
      teamLogo: stat?.team?.logo || null,
      value,
    };
  };

  return leaderboardConfigs
    .map((config) => {
      const rows = players
        .map((entry) => {
          const stat = entry?.statistics?.[0] || {};
          return {
            entry,
            value: config.getValue(stat),
          };
        })
        .filter((x) => x.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 5)
        .map((x) => toLeaderboardEntry(x.entry, x.value));

      return { ...config, rows };
    })
    .filter((board) => board.rows.length > 0);
};

export default function LeaguePage() {
  const { leagueCode } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("table");
  const [tableData, setTableData] = useState(null);
  const [fixturesData, setFixturesData] = useState(null);
  const [scorersData, setScorersData] = useState(null);
  const [leagueStatsData, setLeagueStatsData] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);

  const rawResolvedLeagueCode =
    getLeagueCodeFromAny({ code: leagueCode, name: leagueCode, id: leagueCode }) ||
    String(leagueCode || "").trim().toUpperCase();
  const rawFallbackLeagueId = getLeagueIdFromAny({ code: rawResolvedLeagueCode, id: leagueCode });
  const rawFallbackLeagueName = getLeagueNameFromAny({ code: rawResolvedLeagueCode, name: leagueCode, id: leagueCode });
  const isWorldCup = isWorldCupCompetition({
    league_code: rawResolvedLeagueCode,
    league_id: rawFallbackLeagueId,
    league_name: rawFallbackLeagueName,
  });
  const resolvedLeagueCode = isWorldCup ? WORLD_CUP_CODE : rawResolvedLeagueCode;
  const fallbackLeagueId = isWorldCup ? 1 : rawFallbackLeagueId;
  const fallbackLeagueName = isWorldCup ? "FIFA World Cup 2026" : rawFallbackLeagueName;
  const requestedSeason = isWorldCup ? WORLD_CUP_SEASON : undefined;

  const leagueId =
    firstDefined(tableData?.league_id, fixturesData?.league_id, fallbackLeagueId) || null;
  const leagueName = resolveLeagueDisplayName({
    code: firstDefined(tableData?.league_code, fixturesData?.league_code, resolvedLeagueCode),
    name: firstDefined(tableData?.league_name, fixturesData?.league_name, fallbackLeagueName, leagueCode),
    id: leagueId,
  });
  const season =
    firstDefined(leagueStatsData?.season, tableData?.season, fixturesData?.season, "Current");
  const leagueLogo = getLeagueLogo(tableData, fixturesData);

  // Fetch all data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch table, fixtures, and preferences in parallel
        const [table, fixtures, leagueStats, prefs] = await Promise.all([
          fetchLeagueTable(resolvedLeagueCode, requestedSeason),
          fetchLeagueFixtures(resolvedLeagueCode, requestedSeason),
          isWorldCup ? Promise.resolve(null) : fetchLeagueStats(resolvedLeagueCode).catch(() => null),
          getMyPreferences().catch(() => ({ favorite_leagues: [] })),
        ]);

        setTableData(table);
        setFixturesData(fixtures);
        setLeagueStatsData(leagueStats);
        setIsFollowing(prefs.favorite_leagues?.includes(resolvedLeagueCode) || false);
      } catch (err) {
        console.error("Error loading league data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [resolvedLeagueCode, requestedSeason, isWorldCup]);

  useEffect(() => {
    setScorersData(null);
    setLeagueStatsData(null);
    setActiveTab(isWorldCup ? "fixtures" : "table");
  }, [resolvedLeagueCode, isWorldCup]);

  // Fallback fetch scorers when stats tab is clicked and league stats endpoint isn't available
  useEffect(() => {
    if (activeTab === "stats" && !leagueStatsData && !scorersData && leagueId) {
      const fallbackSeason = firstDefined(tableData?.season, fixturesData?.season);
      const fetchPromise =
        fallbackSeason !== undefined && fallbackSeason !== null && fallbackSeason !== ""
          ? fetchTopScorers(leagueId, fallbackSeason)
          : fetchTopScorers(leagueId);
      fetchPromise
        .then(setScorersData)
        .catch(err => console.error("Error loading top scorers:", err));
    }
  }, [activeTab, leagueId, leagueStatsData, scorersData, tableData, fixturesData]);

  // Toggle follow league
  const handleFollowToggle = async () => {
    try {
      const prefs = await getMyPreferences().catch(() => ({ favorite_leagues: [] }));
      const currentFavs = prefs.favorite_leagues || [];

      const newFavs = isFollowing
        ? currentFavs.filter(code => code !== resolvedLeagueCode)
        : [...currentFavs, resolvedLeagueCode];

      await saveMyPreferences({ favorite_leagues: newFavs });
      setIsFollowing(!isFollowing);
    } catch (err) {
      console.error("Error toggling league follow:", err);
    }
  };

  // Navigate to club page
  const goToClub = (teamId, teamName) => {
    if (!teamId) return;
    const slug = teamName.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "");
    const params = new URLSearchParams();
    if (season) params.set("season", String(season));
    if (leagueId) params.set("league", String(leagueId));
    const qs = params.toString();
    navigate(`/club/${teamId}/${slug}${qs ? `?${qs}` : ""}`);
  };

  // Navigate to fixture page
  const goToFixture = (fixtureId) => {
    if (!fixtureId) return;
    navigate(`/fixture/${fixtureId}`);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading {leagueName}...</div>
      </div>
    );
  }

  if (isWorldCup) {
    return (
      <WorldCupPage
        tableData={tableData}
        fixturesData={fixturesData}
        leagueName={firstDefined(fixturesData?.league_name, tableData?.league_name, "FIFA World Cup 2026")}
        season={WORLD_CUP_SEASON}
        leagueLogo={leagueLogo}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isFollowing={isFollowing}
        handleFollowToggle={handleFollowToggle}
        goToFixture={goToFixture}
        goToClub={goToClub}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        {/* Header */}
        <div className={styles.headerCard}>
          <div className={styles.header}>
            <div className={styles.headerInfoWrap}>
              <div className={styles.leagueLogoWrap}>
                <div className={styles.leagueLogoFallback}>🏆</div>
                {leagueLogo && (
                  <img
                    src={leagueLogo}
                    alt={`${leagueName} logo`}
                    className={styles.leagueLogo}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </div>
              <div className={styles.headerInfo}>
                <h1 className={styles.leagueName}>{leagueName}</h1>
                <p className={styles.season}>Season {season}</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <div className={styles.seasonChip}>{season}</div>
              <button
                className={`${styles.followBtn} ${isFollowing ? styles.following : ""}`}
                onClick={handleFollowToggle}
              >
                {isFollowing ? "Following ✓" : "Follow League"}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === "table" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("table")}
            >
              Table
            </button>
            <button
              className={`${styles.tab} ${activeTab === "fixtures" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("fixtures")}
            >
              Fixtures
            </button>
            <button
              className={`${styles.tab} ${activeTab === "stats" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("stats")}
            >
              Stats
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className={styles.content}>
          {activeTab === "table" && <TableTab data={tableData} fixturesData={fixturesData} goToClub={goToClub} />}
          {activeTab === "fixtures" && <FixturesTab data={fixturesData} goToFixture={goToFixture} />}
          {activeTab === "stats" && (
            <StatsTab
              leagueStatsData={leagueStatsData}
              scorersData={scorersData}
              tableData={tableData}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function WorldCupPage({
  tableData,
  fixturesData,
  leagueName,
  season,
  leagueLogo,
  activeTab,
  setActiveTab,
  isFollowing,
  handleFollowToggle,
  goToFixture,
  goToClub,
}) {
  const fixtures = useMemo(() => normalizeFixturesPayload(fixturesData), [fixturesData]);
  const groups = useMemo(() => normalizeWorldCupGroups(tableData), [tableData]);
  const stageGroups = useMemo(() => groups.filter((group) => isOfficialWorldCupGroup(group.name)), [groups]);
  const teamGroupMap = useMemo(() => buildWorldCupTeamGroupMap(stageGroups), [stageGroups]);
  const groupOptions = useMemo(() => stageGroups.map((group) => group.name), [stageGroups]);
  const kickoffCount = fixtures.length;
  const groupCount = stageGroups.length || groups.length;

  return (
    <div className={`${styles.page} ${styles.worldCupPage}`}>
      <div className={styles.shell}>
        <section className={styles.wcHero}>
          <div className={styles.wcHeroMain}>
            <div className={styles.wcLogoMark}>
              <img
                src="/world-cup-2026-logo.jpeg"
                alt="FIFA World Cup 2026 logo"
                className={styles.wcBrandLogo}
              />
            </div>
            <div>
              <div className={styles.wcKicker}>United States / Mexico / Canada</div>
              <h1 className={styles.wcTitle}>FIFA World Cup 2026</h1>
              <p className={styles.wcSubTitle}>
                The tournament hub for fixtures, group tables, knockout paths, venues, and matchday movement.
              </p>
            </div>
          </div>
          <div className={styles.wcHeroMeta}>
            <div className={styles.wcMetaCard}>
              <span>Season</span>
              <strong>{season}</strong>
            </div>
            <div className={styles.wcMetaCard}>
              <span>Fixtures</span>
              <strong>{kickoffCount || "-"}</strong>
            </div>
            <div className={styles.wcMetaCard}>
              <span>Groups</span>
              <strong>{groupCount || "-"}</strong>
            </div>
            <button
              className={`${styles.wcFollowBtn} ${isFollowing ? styles.following : ""}`}
              onClick={handleFollowToggle}
              type="button"
            >
              {isFollowing ? "Following" : "Follow WC"}
            </button>
          </div>
        </section>

        <div className={styles.wcTabs}>
          {[
            ["fixtures", "Fixtures"],
            ["groups", "Groups"],
            ["knockout", "Knockout"],
            ["fan", "Fan Hub"],
            ["news", "News"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`${styles.wcTab} ${activeTab === key ? styles.wcActiveTab : ""}`}
              onClick={() => setActiveTab(key)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>

        <div className={styles.wcContent}>
          {activeTab === "fixtures" && (
            <WorldCupFixturesTab
              fixtures={fixtures}
              goToFixture={goToFixture}
              groupOptions={groupOptions}
              teamGroupMap={teamGroupMap}
            />
          )}
          {activeTab === "groups" && <WorldCupGroupsTab groups={groups} goToClub={goToClub} />}
          {activeTab === "knockout" && <WorldCupKnockoutTab fixtures={fixtures} goToFixture={goToFixture} />}
          {activeTab === "fan" && <WorldCupHub embedded />}
          {activeTab === "news" && (
            <div className={styles.wcNewsPanel}>
              <SportsNewsSection
                mode="talking-points"
                title="World Cup News"
                limit={9}
                dark
                emptyMessage="No World Cup news is available yet."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getFixtureTeamGroups(fixture, teamGroupMap) {
  const groups = new Set();
  const addGroup = (teamId, teamName) => {
    const byId = teamId ? teamGroupMap.get(`id:${teamId}`) : null;
    const byName = teamName ? teamGroupMap.get(`name:${normalizeWorldCupTeamName(teamName)}`) : null;
    const group = firstDefined(byId, byName);
    if (group) groups.add(group);
  };
  addGroup(fixture.home_team_id, fixture.home_team);
  addGroup(fixture.away_team_id, fixture.away_team);
  const fixtureGroup = firstDefined(fixture.group, worldCupGroupFromRound(fixture.round));
  if (fixtureGroup && isOfficialWorldCupGroup(fixtureGroup)) groups.add(fixtureGroup);
  return groups;
}

function statDisplay(value) {
  return value === undefined || value === null || value === "" ? "-" : value;
}

function worldCupGroupFromRound(round = "") {
  const match = String(round).match(/group\s+([a-l])/i);
  return match ? `Group ${match[1].toUpperCase()}` : "";
}

function isKnockoutFixture(fixture) {
  const round = String(fixture.round || "").toLowerCase();
  return KNOCKOUT_ROUND_HINTS.some((hint) => round.includes(hint));
}

function knockoutRoundKey(round = "") {
  const text = String(round || "").toLowerCase();
  if (text.includes("round of 32") || text.includes("32nd finals")) return "round32";
  if (text.includes("round of 16") || text.includes("16th finals") || text.includes("8th finals")) return "round16";
  if (text.includes("quarter")) return "quarter";
  if (text.includes("semi")) return "semi";
  if (text.includes("third")) return "third";
  if (text.includes("final")) return "final";
  return null;
}

function fixtureWinnerLabel(fixture) {
  const homeScore = Number(fixture.home_score);
  const awayScore = Number(fixture.away_score);
  if (isFinishedStatus(fixture.status) && Number.isFinite(homeScore) && Number.isFinite(awayScore)) {
    if (homeScore > awayScore) return fixture.home_team;
    if (awayScore > homeScore) return fixture.away_team;
  }
  return null;
}

function fixtureDateLabel(dateValue) {
  const date = toDateValue(dateValue);
  if (!date) return "TBD";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function fixtureTimeLabel(dateValue) {
  const date = toDateValue(dateValue);
  if (!date) return "TBD";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fixtureScoreLabel(fixture) {
  const hasScore = fixture.home_score !== null && fixture.home_score !== undefined && fixture.away_score !== null && fixture.away_score !== undefined;
  if (isFinishedStatus(fixture.status) && hasScore) return `${fixture.home_score} - ${fixture.away_score}`;
  if (isLiveStatus(fixture.status) && hasScore) return `${fixture.home_score} - ${fixture.away_score}`;
  return fixtureTimeLabel(fixture.date);
}

function WorldCupFixturesTab({ fixtures, goToFixture, groupOptions = [], teamGroupMap = new Map() }) {
  const [filters, setFilters] = useState({ date: "", round: "", team: "", group: "" });

  const options = useMemo(() => {
    const dates = new Map();
    const rounds = new Set();
    const teams = new Set();
    fixtures.forEach((fixture) => {
      const date = toDateValue(fixture.date);
      if (date) {
        const key = date.toISOString().slice(0, 10);
        dates.set(key, fixtureDateLabel(fixture.date));
      }
      if (fixture.round) rounds.add(fixture.round);
      if (fixture.home_team) teams.add(fixture.home_team);
      if (fixture.away_team) teams.add(fixture.away_team);
    });
    return {
      dates: [...dates.entries()].sort(([a], [b]) => a.localeCompare(b)),
      rounds: [...rounds].sort(),
      teams: [...teams].sort(),
      groups: groupOptions,
    };
  }, [fixtures, groupOptions]);

  const filteredFixtures = useMemo(() => fixtures.filter((fixture) => {
    const fixtureDate = toDateValue(fixture.date)?.toISOString().slice(0, 10) || "";
    const fixtureGroups = getFixtureTeamGroups(fixture, teamGroupMap);
    if (filters.date && fixtureDate !== filters.date) return false;
    if (filters.round && fixture.round !== filters.round) return false;
    if (filters.team && fixture.home_team !== filters.team && fixture.away_team !== filters.team) return false;
    if (filters.group && !fixtureGroups.has(filters.group)) return false;
    return true;
  }), [filters, fixtures, teamGroupMap]);

  const grouped = useMemo(() => groupFixturesForTournament(filteredFixtures), [filteredFixtures]);

  if (!fixtures.length) {
    return <div className={styles.wcEmptyState}>World Cup fixtures have not landed yet.</div>;
  }

  return (
    <div className={styles.wcTabStack}>
      <div className={styles.wcFilters}>
        <FilterSelect label="Date" value={filters.date} onChange={(date) => setFilters((prev) => ({ ...prev, date }))} options={options.dates.map(([value, label]) => ({ value, label }))} />
        <FilterSelect label="Round" value={filters.round} onChange={(round) => setFilters((prev) => ({ ...prev, round }))} options={options.rounds.map((round) => ({ value: round, label: round }))} />
        <FilterSelect label="Team" value={filters.team} onChange={(team) => setFilters((prev) => ({ ...prev, team }))} options={options.teams.map((team) => ({ value: team, label: team }))} />
        <FilterSelect label="Group" value={filters.group} onChange={(group) => setFilters((prev) => ({ ...prev, group }))} options={options.groups.map((group) => ({ value: group, label: group }))} />
      </div>

      {!filteredFixtures.length ? (
        <div className={styles.wcEmptyState}>No fixtures match those filters.</div>
      ) : (
        grouped.map(({ title, fixtures: groupFixtures }) => (
          <section key={title} className={styles.wcMatchday}>
            <div className={styles.wcSectionHeader}>
              <h2>{title}</h2>
              <span>{groupFixtures.length} matches</span>
            </div>
            <div className={styles.wcFixtureList}>
              {groupFixtures.map((fixture, index) => (
                <WorldCupFixtureRow
                  key={fixture.internal_route_id || fixture.id || `${title}-${index}`}
                  fixture={fixture}
                  goToFixture={goToFixture}
                />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function groupFixturesForTournament(fixtures) {
  const byRound = new Map();
  [...fixtures]
    .sort((a, b) => (toDateValue(a.date)?.getTime() || Infinity) - (toDateValue(b.date)?.getTime() || Infinity))
    .forEach((fixture) => {
      const key = fixture.round || "Tournament Fixtures";
      if (!byRound.has(key)) byRound.set(key, []);
      byRound.get(key).push(fixture);
    });
  return [...byRound.entries()].map(([title, groupFixtures]) => ({ title, fixtures: groupFixtures }));
}

function buildHybridKnockoutBracket(fixtures) {
  const byRound = new Map();
  [...fixtures]
    .sort((a, b) => (toDateValue(a.date)?.getTime() || Infinity) - (toDateValue(b.date)?.getTime() || Infinity))
    .forEach((fixture) => {
      const key = knockoutRoundKey(fixture.round);
      if (!key) return;
      if (!byRound.has(key)) byRound.set(key, []);
      byRound.get(key).push(fixture);
    });

  const rounds = [];
  let previousEntries = [];

  for (const round of KNOCKOUT_ROUNDS) {
    const confirmed = byRound.get(round.key) || [];
    let entries = [];

    if (confirmed.length) {
      entries = confirmed.map((fixture, index) => {
        const label = `${round.short} ${index + 1}`;
        return {
          type: "fixture",
          label,
          fixture,
          home: fixture.home_team,
          away: fixture.away_team,
          winner: fixtureWinnerLabel(fixture) || `Winner ${label}`,
        };
      });
    } else if (previousEntries.length >= 2) {
      entries = [];
      for (let index = 0; index < previousEntries.length; index += 2) {
        const left = previousEntries[index];
        const right = previousEntries[index + 1];
        if (!left || !right) continue;
        const label = `${round.short} ${entries.length + 1}`;
        entries.push({
          type: "projected",
          label,
          home: left.winner,
          away: right.winner,
          winner: `Winner ${label}`,
        });
      }
    }

    rounds.push({ ...round, entries });
    previousEntries = entries;
  }

  const semiRound = rounds.find((round) => round.key === "semi");
  if (semiRound?.entries?.length >= 2 && !(byRound.get("third") || []).length) {
    rounds.push({
      key: "third",
      title: "Third-place playoff",
      short: "3rd",
      entries: [
        {
          type: "projected",
          label: "3rd",
          home: `Loser ${semiRound.entries[0].label}`,
          away: `Loser ${semiRound.entries[1].label}`,
          winner: "Third place",
        },
      ],
    });
  } else if ((byRound.get("third") || []).length) {
    rounds.push({
      key: "third",
      title: "Third-place playoff",
      short: "3rd",
      entries: (byRound.get("third") || []).map((fixture, index) => ({
        type: "fixture",
        label: `3rd ${index + 1}`,
        fixture,
        home: fixture.home_team,
        away: fixture.away_team,
        winner: fixtureWinnerLabel(fixture) || "Third place",
      })),
    });
  }

  return rounds.filter((round) => round.entries.length);
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className={styles.wcFilter}>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">All</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function WorldCupFixtureRow({ fixture, goToFixture }) {
  const routeId = firstDefined(fixture.internal_route_id, fixture.route_id && !fixture.provider_fixture_id ? fixture.route_id : null);
  const canOpen = Boolean(routeId);
  const venueLine = [fixture.venue, fixture.city].filter(Boolean).join(" / ");

  return (
    <button
      type="button"
      className={`${styles.wcFixtureRow} ${canOpen ? "" : styles.wcFixtureDisabled}`}
      onClick={() => canOpen && goToFixture(routeId)}
      disabled={!canOpen}
    >
      <div className={styles.wcFixtureTime}>
        <strong>{fixtureDateLabel(fixture.date)}</strong>
        <span>{fixtureTimeLabel(fixture.date)}</span>
      </div>
      <TeamSide name={fixture.home_team} logo={fixture.home_logo} align="left" />
      <div className={styles.wcScoreBox}>
        <strong>{fixtureScoreLabel(fixture)}</strong>
        <span>{isLiveStatus(fixture.status) ? "Live" : isFinishedStatus(fixture.status) ? "FT" : fixture.status || "Kickoff"}</span>
      </div>
      <TeamSide name={fixture.away_team} logo={fixture.away_logo} align="right" />
      <div className={styles.wcVenue}>
        <span>{canOpen ? venueLine || "Venue TBD" : "Fixture page pending"}</span>
      </div>
    </button>
  );
}

function TeamSide({ name, logo, align }) {
  return (
    <div className={`${styles.wcTeamSide} ${align === "right" ? styles.wcTeamRight : ""}`}>
      <img
        src={logo || fallbackLogoForTeam(name)}
        alt={name}
        onError={(event) => applyLogoFallback(event, name)}
      />
      <span>{name}</span>
    </div>
  );
}

function WorldCupGroupsTab({ groups, goToClub }) {
  if (!groups.length) {
    return <div className={styles.wcEmptyState}>Group standings will appear when the tournament table is available.</div>;
  }

  return (
    <div className={styles.wcGroupsGrid}>
      {groups.map((group) => (
        <section key={group.name} className={styles.wcGroupCard}>
          <div className={styles.wcGroupTitle}>
            <span>{group.name}</span>
            <em>{group.rows.length} teams</em>
          </div>
          <table className={styles.wcGroupTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Team</th>
                <th>P</th>
                <th>W</th>
                <th>D</th>
                <th>L</th>
                <th>GF</th>
                <th>GA</th>
                <th>GD</th>
                <th>Pts</th>
                <th>Form</th>
              </tr>
            </thead>
            <tbody>
              {group.rows.map((row, idx) => {
                const gf = row.gf;
                const ga = row.ga;
                const gd = gf === null || gf === undefined || ga === null || ga === undefined ? "-" : asNumber(gf) - asNumber(ga);
                return (
                  <tr key={`${group.name}-${row.team_id || row.team || idx}`}>
                    <td>{statDisplay(row.position) === "-" ? idx + 1 : row.position}</td>
                    <td>
                      <button type="button" className={styles.wcGroupTeam} onClick={() => goToClub(row.team_id, row.team)} disabled={!row.team_id}>
                        <img src={row.team_logo || fallbackLogoForTeam(row.team)} alt={row.team} onError={(event) => applyLogoFallback(event, row.team)} />
                        <span>{row.team}</span>
                      </button>
                    </td>
                    <td>{statDisplay(row.played)}</td>
                    <td>{statDisplay(row.win)}</td>
                    <td>{statDisplay(row.draw)}</td>
                    <td>{statDisplay(row.lose)}</td>
                    <td>{statDisplay(row.gf)}</td>
                    <td>{statDisplay(row.ga)}</td>
                    <td>{gd}</td>
                    <td className={styles.wcGroupPts}>{statDisplay(row.points)}</td>
                    <td><FormCircles form={row.form} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

function WorldCupKnockoutTab({ fixtures, goToFixture }) {
  const knockoutFixtures = useMemo(() => fixtures.filter(isKnockoutFixture), [fixtures]);
  const bracketRounds = useMemo(() => buildHybridKnockoutBracket(knockoutFixtures), [knockoutFixtures]);

  if (!knockoutFixtures.length) {
    return (
      <div className={styles.wcKnockoutEmpty}>
        <div className={styles.wcKnockoutBadge}>Knockout</div>
        <h2>Knockout path unlocks after group stage</h2>
        <p>Round of 16, quarter-finals, semi-finals, third-place playoff, and final fixtures will appear here when the API provides confirmed matchups.</p>
      </div>
    );
  }

  return (
    <div className={styles.wcTabStack}>
      <section className={styles.wcBracketPanel}>
        <div className={styles.wcSectionHeader}>
          <h2>Live knockout bracket</h2>
          <span>Confirmed + projected path</span>
        </div>
        <div className={styles.wcBracketScroller}>
          <div className={styles.wcBracketGrid}>
            {bracketRounds.map((round) => (
              <div key={round.key} className={styles.wcBracketRound}>
                <div className={styles.wcBracketRoundTitle}>
                  <strong>{round.title}</strong>
                  <span>{round.entries.length} slots</span>
                </div>
                <div className={styles.wcBracketCards}>
                  {round.entries.map((entry, index) => (
                    <WorldCupBracketCard
                      key={`${round.key}-${entry.fixture?.provider_fixture_id || entry.label}-${index}`}
                      entry={entry}
                      goToFixture={goToFixture}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {groupFixturesForTournament(knockoutFixtures).map(({ title, fixtures: groupFixtures }) => (
        <section key={`list-${title}`} className={styles.wcMatchday}>
          <div className={styles.wcSectionHeader}>
            <h2>{title}</h2>
            <span>Confirmed fixtures</span>
          </div>
          <div className={styles.wcFixtureList}>
            {groupFixtures.map((fixture, index) => (
              <WorldCupFixtureRow
                key={fixture.internal_route_id || fixture.id || `${title}-${index}`}
                fixture={fixture}
                goToFixture={goToFixture}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function WorldCupBracketCard({ entry, goToFixture }) {
  if (entry.type === "fixture") {
    const fixture = entry.fixture;
    const routeId = firstDefined(fixture.internal_route_id, fixture.route_id && !fixture.provider_fixture_id ? fixture.route_id : null);
    const canOpen = Boolean(routeId);
    return (
      <button
        type="button"
        className={`${styles.wcBracketCard} ${canOpen ? "" : styles.wcBracketCardDisabled}`}
        onClick={() => canOpen && goToFixture(routeId)}
        disabled={!canOpen}
      >
        <div className={styles.wcBracketMeta}>
          <span>{entry.label}</span>
          <em>{fixtureDateLabel(fixture.date)} · {fixtureScoreLabel(fixture)}</em>
        </div>
        <div className={styles.wcBracketTeam}>{fixture.home_team}</div>
        <div className={styles.wcBracketTeam}>{fixture.away_team}</div>
      </button>
    );
  }

  return (
    <div className={`${styles.wcBracketCard} ${styles.wcBracketProjected}`}>
      <div className={styles.wcBracketMeta}>
        <span>{entry.label}</span>
        <em>Projected until confirmed</em>
      </div>
      <div className={styles.wcBracketTeam}>{entry.home}</div>
      <div className={styles.wcBracketTeam}>{entry.away}</div>
    </div>
  );
}

// ===== TABLE TAB =====
function TableTab({ data, fixturesData, goToClub }) {
  const [mode, setMode] = useState("all");
  const rows = useMemo(() => normalizeTablePayload(data), [data]);
  const fixtures = useMemo(() => normalizeFixturesPayload(fixturesData), [fixturesData]);

  const normalizeTeamKey = (value = "") => String(value).trim().toLowerCase().replace(/\s+/g, " ");

  const nextByTeam = useMemo(() => {
    const map = new Map();
    const now = Date.now();
    const upcoming = [...fixtures]
      .filter((fixture) => {
        const dt = toDateValue(fixture.date)?.getTime();
        if (isFinishedStatus(fixture.status)) return false;
        return !dt || dt >= now;
      })
      .sort((a, b) => {
        const aDate = toDateValue(a.date)?.getTime() || Infinity;
        const bDate = toDateValue(b.date)?.getTime() || Infinity;
        return aDate - bDate;
      });

    const setNext = (teamId, teamName, opponentName, opponentLogo) => {
      const idKey = teamId ? `id:${teamId}` : null;
      const nameKey = teamName ? `name:${normalizeTeamKey(teamName)}` : null;
      const nextInfo = { name: opponentName || "TBD", logo: opponentLogo || null };
      if (idKey && !map.has(idKey)) map.set(idKey, nextInfo);
      if (nameKey && !map.has(nameKey)) map.set(nameKey, nextInfo);
    };

    upcoming.forEach((fixture) => {
      setNext(fixture.home_team_id, fixture.home_team, fixture.away_team, fixture.away_logo);
      setNext(fixture.away_team_id, fixture.away_team, fixture.home_team, fixture.home_logo);
    });

    return map;
  }, [fixtures]);

  const rowsForMode = useMemo(() => {
    const buildLast5Stats = (row) => {
      const letters = String(row.form || "")
        .toUpperCase()
        .replace(/[^WDL]/g, "")
        .split("")
        .slice(-5);
      if (!letters.length) return row.last5;
      const win = letters.filter((x) => x === "W").length;
      const draw = letters.filter((x) => x === "D").length;
      const lose = letters.filter((x) => x === "L").length;
      return {
        played: letters.length,
        win,
        draw,
        lose,
        gf: firstDefined(row.last5?.gf, null),
        ga: firstDefined(row.last5?.ga, null),
        points: (win * 3) + draw,
        form: letters.join(""),
      };
    };

    const withStats = rows.map((row) => {
      const stats =
        mode === "home" ? row.home :
        mode === "away" ? row.away :
        mode === "last5" ? buildLast5Stats(row) :
        row;

      const idKey = row.team_id ? `id:${row.team_id}` : null;
      const nameKey = row.team ? `name:${normalizeTeamKey(row.team)}` : null;
      const nextFromTable =
        row.next_opponent || row.next_opponent_logo
          ? { name: row.next_opponent || "TBD", logo: row.next_opponent_logo || null }
          : null;
      const nextFromFixtures =
        (idKey && nextByTeam.get(idKey)) || (nameKey && nextByTeam.get(nameKey)) || null;

      return {
        ...row,
        modeStats: stats,
        nextOpponent: nextFromFixtures || nextFromTable,
      };
    });

    if (mode === "all") {
      return withStats.map((row, idx) => ({
        ...row,
        displayPosition: Number(row.position) || idx + 1,
      }));
    }

    return withStats
      .sort((a, b) => {
        const aStats = a.modeStats || {};
        const bStats = b.modeStats || {};
        const aPts = asNumber(aStats.points);
        const bPts = asNumber(bStats.points);
        if (aPts !== bPts) return bPts - aPts;
        const aGd = asNumber(aStats.gf) - asNumber(aStats.ga);
        const bGd = asNumber(bStats.gf) - asNumber(bStats.ga);
        if (aGd !== bGd) return bGd - aGd;
        const aGf = asNumber(aStats.gf);
        const bGf = asNumber(bStats.gf);
        if (aGf !== bGf) return bGf - aGf;
        return String(a.team).localeCompare(String(b.team));
      })
      .map((row, idx) => ({ ...row, displayPosition: idx + 1 }));
  }, [rows, mode, nextByTeam]);

  if (!rowsForMode.length) {
    return <div className={styles.noData}>No standings data available</div>;
  }

  const getPositionClass = (position) => {
    if (mode !== "all") return "";
    const numericPosition = Number(position);
    if (numericPosition <= 4) return styles.ucl;
    if (numericPosition <= 6) return styles.europa;
    if (numericPosition >= rowsForMode.length - 2) return styles.relegation;
    return "";
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableFilters}>
        <button type="button" className={`${styles.tableFilterBtn} ${mode === "all" ? styles.tableFilterActive : ""}`} onClick={() => setMode("all")}>All</button>
        <button type="button" className={`${styles.tableFilterBtn} ${mode === "home" ? styles.tableFilterActive : ""}`} onClick={() => setMode("home")}>Home</button>
        <button type="button" className={`${styles.tableFilterBtn} ${mode === "away" ? styles.tableFilterActive : ""}`} onClick={() => setMode("away")}>Away</button>
        <button type="button" className={`${styles.tableFilterBtn} ${mode === "last5" ? styles.tableFilterActive : ""}`} onClick={() => setMode("last5")}>Last 5</button>
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>#</th>
            <th>Team</th>
            <th>P</th>
            <th>W</th>
            <th>D</th>
            <th>L</th>
            <th>GF</th>
            <th>GA</th>
            <th>GD</th>
            <th>Pts</th>
            <th>Form</th>
            <th>Next</th>
          </tr>
        </thead>
        <tbody>
          {rowsForMode.map((row) => {
            const stats = row.modeStats || {};
            const gf = stats.gf;
            const ga = stats.ga;
            const gd = asNumber(gf) - asNumber(ga);
            return (
              <tr key={`${mode}-${row.team_id || row.team}`} className={getPositionClass(row.displayPosition)}>
                <td>{row.displayPosition}</td>
                <td className={styles.teamCell} onClick={() => goToClub(row.team_id, row.team)}>
                  <img
                    src={row.team_logo || fallbackLogoForTeam(row.team)}
                    alt={row.team}
                    className={styles.teamLogo}
                    onError={(event) => applyLogoFallback(event, row.team)}
                  />
                  <span className={styles.teamName}>{row.team}</span>
                </td>
                <td>{stats.played}</td>
                <td>{stats.win}</td>
                <td>{stats.draw}</td>
                <td>{stats.lose}</td>
                <td>{gf ?? "-"}</td>
                <td>{ga ?? "-"}</td>
                <td>{(gf ?? null) === null || (ga ?? null) === null ? "-" : gd}</td>
                <td className={styles.points}>{stats.points}</td>
                <td>
                  <FormCircles form={stats.form} />
                </td>
                <td>
                  {row.nextOpponent ? (
                    <span className={styles.nextTeam}>
                      {row.nextOpponent.logo ? (
                        <img
                          src={row.nextOpponent.logo}
                          alt={row.nextOpponent.name}
                          className={styles.nextTeamLogo}
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      ) : null}
                      <span>{row.nextOpponent.name}</span>
                    </span>
                  ) : (
                    <span className={styles.noForm}>-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ===== FIXTURES TAB =====
function FixturesTab({ data, goToFixture }) {
  const roundRefs = useRef({});
  const hasScrolledRef = useRef(false);
  const fixtures = useMemo(
    () => normalizeFixturesPayload(data),
    [data]
  );

  const roundEntries = useMemo(() => {
    const byRound = {};
    fixtures.forEach((fixture) => {
      const round = fixture.round || "Unknown";
      if (!byRound[round]) byRound[round] = [];
      byRound[round].push(fixture);
    });

    const entries = Object.entries(byRound).map(([round, fixtures]) => {
      const sortedFixtures = [...fixtures].sort((a, b) => {
        const aDate = toDateValue(a.date)?.getTime() || Infinity;
        const bDate = toDateValue(b.date)?.getTime() || Infinity;
        return aDate - bDate;
      });
      const firstFixtureDate = sortedFixtures[0]?.date ? toDateValue(sortedFixtures[0].date)?.getTime() || Infinity : Infinity;
      return {
        round,
        fixtures: sortedFixtures,
        firstFixtureDate,
        roundNumber: extractRoundNumber(round),
      };
    });

    entries.sort((a, b) => {
      if (a.firstFixtureDate !== b.firstFixtureDate) {
        return a.firstFixtureDate - b.firstFixtureDate;
      }
      if (a.roundNumber !== null && b.roundNumber !== null && a.roundNumber !== b.roundNumber) {
        return a.roundNumber - b.roundNumber;
      }
      return a.round.localeCompare(b.round);
    });

    return entries;
  }, [fixtures]);

  const upcomingRound = useMemo(() => {
    if (!roundEntries.length) return null;

    const now = Date.now();
    const allFixtures = roundEntries.flatMap((entry) =>
      entry.fixtures.map((fixture) => ({
        ...fixture,
        round: entry.round,
        fixtureDate: toDateValue(fixture.date)?.getTime() || null,
      }))
    );

    const liveRound = allFixtures.find((fixture) => isLiveStatus(fixture.status));
    if (liveRound) return liveRound.round;

    const nextFixture = allFixtures
      .filter((fixture) => !isFinishedStatus(fixture.status) && fixture.fixtureDate && fixture.fixtureDate >= now)
      .sort((a, b) => a.fixtureDate - b.fixtureDate)[0];
    if (nextFixture) return nextFixture.round;

    return roundEntries[roundEntries.length - 1].round;
  }, [roundEntries]);

  useEffect(() => {
    hasScrolledRef.current = false;
  }, [upcomingRound]);

  useEffect(() => {
    if (!upcomingRound || hasScrolledRef.current) return;
    const target = roundRefs.current[upcomingRound];
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "start" });
    hasScrolledRef.current = true;
  }, [upcomingRound, roundEntries]);

  if (!fixtures.length) {
    return <div className={styles.noData}>No fixtures data available</div>;
  }

  return (
    <div className={styles.fixturesContainer}>
      {roundEntries.map(({ round, fixtures }) => (
        <div
          key={round}
          ref={(el) => {
            if (el) roundRefs.current[round] = el;
          }}
          className={`${styles.roundGroup} ${round === upcomingRound ? styles.upcomingRound : ""}`}
        >
          <h3 className={styles.roundHeader}>
            {round}
            {round === upcomingRound && <span className={styles.upcomingBadge}>Upcoming</span>}
          </h3>
          {fixtures.map((f, index) => {
            const canOpenFixture = Boolean(f?.route_id);
            return (
            <div
              key={f.route_id || f.id || `${round}-${index}`}
              className={`${styles.fixtureRow} ${canOpenFixture ? "" : styles.fixtureRowDisabled}`}
              onClick={() => canOpenFixture && goToFixture(f.route_id)}
            >
              <div className={styles.fixtureDate}>
                {toDateValue(f.date)?.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                }) || "TBD"}
              </div>
              <div className={styles.fixtureTeams}>
                <div className={styles.fixtureTeam}>
                  <img
                    src={f.home_logo || fallbackLogoForTeam(f.home_team)}
                    alt={f.home_team}
                    className={styles.fixtureLogo}
                    onError={(event) => applyLogoFallback(event, f.home_team)}
                  />
                  <span>{f.home_team}</span>
                </div>
                <div className={styles.fixtureScore}>
                  {isFinishedStatus(f.status)
                    ? (f.home_score !== null && f.away_score !== null ? `${f.home_score} - ${f.away_score}` : "FT")
                    : toDateValue(f.date)?.toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      }) || "TBD"}
                </div>
                <div className={styles.fixtureTeam}>
                  <img
                    src={f.away_logo || fallbackLogoForTeam(f.away_team)}
                    alt={f.away_team}
                    className={styles.fixtureLogo}
                    onError={(event) => applyLogoFallback(event, f.away_team)}
                  />
                  <span>{f.away_team}</span>
                </div>
              </div>
              <div className={styles.fixtureStatus}>
                {isFinishedStatus(f.status) ? "FT" : isLiveStatus(f.status) ? "LIVE" : ""}
              </div>
            </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ===== STATS TAB =====
function StatsTab({ leagueStatsData, scorersData, tableData }) {
  const [drilldown, setDrilldown] = useState(null);

  useEffect(() => {
    if (!drilldown) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setDrilldown(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drilldown]);

  if (!leagueStatsData && !scorersData && !tableData) {
    return <div className={styles.noData}>Loading stats...</div>;
  }

  const table = normalizeTablePayload(tableData);
  const fallbackPlayerBoards = buildPlayerLeaderboards(scorersData);

  const teamWithMetric = (row, value, label) => ({
    team: row.team,
    team_id: row.team_id,
    team_logo: row.team_logo,
    value,
    label,
  });

  const teamByGoalsPerMatch = [...table]
    .map((row) => ({ row, value: row.played ? row.gf / row.played : 0 }))
    .sort((a, b) => b.value - a.value);

  const teamByGoalsConcededPerMatch = [...table]
    .map((row) => ({ row, value: row.played ? row.ga / row.played : 0 }))
    .sort((a, b) => a.value - b.value);

  const fallbackTeamRankings = {
    goals_per_match: teamByGoalsPerMatch.map((x) => teamWithMetric(x.row, Number(x.value.toFixed(2)), "per match")),
    goals_conceded_per_match: teamByGoalsConcededPerMatch.map((x) =>
      teamWithMetric(x.row, Number(x.value.toFixed(2)), "per match")
    ),
    possession: [],
    clean_sheets: [],
  };

  const fallbackTeamLeaders = {
    goals_per_match: fallbackTeamRankings.goals_per_match[0] || null,
    goals_conceded_per_match: fallbackTeamRankings.goals_conceded_per_match[0] || null,
    possession: null,
    clean_sheets: null,
  };

  const teamLeaders = leagueStatsData?.team_leaders || fallbackTeamLeaders;
  const teamRankings = leagueStatsData?.team_rankings || fallbackTeamRankings;

  const fallbackPlayerRankings = Object.fromEntries(
    fallbackPlayerBoards.map((board) => [
      board.key,
      board.rows.map((row) => ({
        value: row.value,
        label: board.unit,
        player: { id: row.playerId, name: row.player, photo: row.playerPhoto },
        statistics: { team: { id: row.teamId, name: row.team, logo: row.teamLogo } },
      })),
    ])
  );

  const playerLeaders = leagueStatsData?.player_leaders || {};
  const playerRankings = leagueStatsData?.player_rankings || fallbackPlayerRankings;

  const teamCards = [
    { key: "goals_per_match", title: "Goals Per Match" },
    { key: "goals_conceded_per_match", title: "Goals Conceded Per Match" },
    { key: "possession", title: "Possession" },
    { key: "clean_sheets", title: "Clean Sheets" },
  ];

  const playerCards = [
    { key: "top_scorer", title: "Top Scorer" },
    { key: "top_assists", title: "Top Assists" },
    { key: "goals_plus_assists", title: "Goals + Assists" },
    { key: "most_yellow_cards", title: "Most Yellow Cards" },
    { key: "most_red_cards", title: "Most Red Cards" },
    { key: "most_shots", title: "Most Shots" },
    { key: "most_shots_on_target", title: "Most Shots on Target" },
    { key: "most_penalties_scored", title: "Most Penalties Scored" },
  ];

  const openDrilldown = (title, type, rows, emptyText) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setDrilldown({ title, type, rows, emptyText });
  };

  const renderTeamTopRows = (rows, leader, title) => {
    const topRows = Array.isArray(rows) && rows.length > 0 ? rows.slice(0, 3) : leader ? [leader] : [];
    if (!topRows.length) {
      return <div className={styles.statEmpty}>{title === "Possession" ? "No possession data" : "No data"}</div>;
    }
    return topRows.map((row, idx) => {
      const teamName = firstDefined(row?.team, row?.team_name, row?.name, "Unknown");
      const teamLogo = firstDefined(row?.team_logo, row?.logo, null);
      return (
        <div key={`${teamName}-${idx}`} className={styles.statRow}>
          <span className={styles.statRank}>{idx + 1}</span>
          <span className={styles.statPlayer}>
            <span className={styles.statTeam}>
              {teamLogo && <img src={teamLogo} alt={teamName} className={styles.statTeamLogo} />}
              {teamName}
            </span>
          </span>
          <span className={styles.statValue}>
            {row?.value}
            {row?.label ? ` ${row.label}` : ""}
          </span>
        </div>
      );
    });
  };

  const renderPlayerTopRows = (rows, leader) => {
    const topRows = Array.isArray(rows) && rows.length > 0 ? rows.slice(0, 3) : leader ? [leader] : [];
    if (!topRows.length) return <div className={styles.statEmpty}>No data</div>;
    return topRows.map((row, idx) => {
      const playerName = firstDefined(row?.player?.name, row?.name, "Unknown");
      const playerPhoto = firstDefined(row?.player?.photo, null);
      const teamName = firstDefined(row?.statistics?.team?.name, row?.team, row?.team_name, "");
      const teamLogo = firstDefined(row?.statistics?.team?.logo, row?.team_logo, row?.logo, null);
      return (
        <div key={`${playerName}-${teamName}-${idx}`} className={styles.statRow}>
          <span className={styles.statRank}>{idx + 1}</span>
          <span className={styles.statPlayer}>
            <span className={styles.statPlayerMain}>
              {playerPhoto && <img src={playerPhoto} alt={playerName} className={styles.statPlayerPhoto} />}
              {playerName}
            </span>
            {teamName ? (
              <span className={styles.statTeam}>
                {teamLogo && <img src={teamLogo} alt={teamName} className={styles.statTeamLogo} />}
                {teamName}
              </span>
            ) : null}
          </span>
          <span className={styles.statValue}>
            {row?.value}
            {row?.label ? ` ${row.label}` : ""}
          </span>
        </div>
      );
    });
  };

  return (
    <>
      <div className={styles.statsSection}>
        <div className={styles.statsHeader}>Team Leaders</div>
        <div className={styles.statsGrid}>
          {teamCards.map((card) => {
            const leader = teamLeaders?.[card.key];
            const rows = teamRankings?.[card.key] || [];
            return (
              <button
                key={card.key}
                type="button"
                className={`${styles.statCard} ${styles.statCardButton}`}
                onClick={() => openDrilldown(card.title, "team", rows, "No ranking data")}
              >
                <h3>{card.title}</h3>
                {renderTeamTopRows(rows, leader, card.title)}
              </button>
            );
          })}
        </div>

        <div className={styles.statsHeader}>Player Leaders</div>
        <div className={styles.statsGrid}>
          {playerCards.map((card) => {
            const leader = playerLeaders?.[card.key] || (playerRankings?.[card.key] || [])[0] || null;
            const rows = playerRankings?.[card.key] || [];
            return (
              <button
                key={card.key}
                type="button"
                className={`${styles.statCard} ${styles.statCardButton}`}
                onClick={() => openDrilldown(card.title, "player", rows, "No ranking data")}
              >
                <h3>{card.title}</h3>
                {renderPlayerTopRows(rows, leader)}
              </button>
            );
          })}
        </div>
      </div>

      {drilldown && (
        <div className={styles.statsModalOverlay} onClick={() => setDrilldown(null)}>
          <div className={styles.statsModal} onClick={(event) => event.stopPropagation()}>
            <div className={styles.statsModalHeader}>
              <h3>{drilldown.title} Rankings</h3>
              <button type="button" className={styles.statsModalClose} onClick={() => setDrilldown(null)}>
                Close
              </button>
            </div>
            <div className={styles.statsModalList}>
              {drilldown.rows.length === 0 ? (
                <div className={styles.statEmpty}>{drilldown.emptyText || "No ranking data"}</div>
              ) : (
                drilldown.rows.map((row, idx) => {
                  const teamName =
                    firstDefined(row?.team, row?.team_name, row?.statistics?.team?.name, row?.name) || "Unknown";
                  const teamLogo = firstDefined(row?.team_logo, row?.logo, row?.statistics?.team?.logo, null);
                  const playerName = firstDefined(row?.player?.name, row?.name, null);
                  const playerPhoto = firstDefined(row?.player?.photo, null);
                  return (
                    <div key={`${teamName}-${playerName || "team"}-${idx}`} className={styles.statsModalRow}>
                      <span className={styles.statsModalRank}>{idx + 1}</span>
                      <span className={styles.statPlayer}>
                        {drilldown.type === "player" ? (
                          <>
                            <span className={styles.statPlayerMain}>
                              {playerPhoto && <img src={playerPhoto} alt={playerName} className={styles.statPlayerPhoto} />}
                              {playerName || "Unknown"}
                            </span>
                            <span className={styles.statTeam}>
                              {teamLogo && <img src={teamLogo} alt={teamName} className={styles.statTeamLogo} />}
                              {teamName}
                            </span>
                          </>
                        ) : (
                          <span className={styles.statTeam}>
                            {teamLogo && <img src={teamLogo} alt={teamName} className={styles.statTeamLogo} />}
                            {teamName}
                          </span>
                        )}
                      </span>
                      <span className={styles.statValue}>
                        {row?.value}
                        {row?.label ? ` ${row.label}` : ""}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ===== FORM CIRCLES =====
function FormCircles({ form }) {
  if (!form) return <span className={styles.noForm}>-</span>;

  const circles = form.split("").slice(-5).map((letter, i) => {
    const className =
      letter === "W"
        ? styles.formW
        : letter === "D"
        ? styles.formD
        : styles.formL;
    return (
      <span key={i} className={`${styles.formCircle} ${className}`}>
        {letter}
      </span>
    );
  });

  return <div className={styles.formContainer}>{circles}</div>;
}
